import {BaseButton, BaseInput, BaseModal, BaseSelect} from "@/components";
import {useUpdateProviderMethod} from "@/hooks/useProviderMethods";
import type {ProviderMethod} from "@/types/provider-method.types";
import {useState} from "react";

interface EditProviderMethodModalProps {
  provider: ProviderMethod;
  onClose: () => void;
}

const categoryOptions = [
  {value: "bank_transfer", label: "Transferencia bancaria"},
  {value: "crypto", label: "Crypto"},
  {value: "card", label: "Tarjeta"},
  {value: "cash", label: "Efectivo"},
  {value: "ewallet", label: "E-Wallet"},
];

export const EditProviderMethodModal = ({provider, onClose}: EditProviderMethodModalProps) => {
  const updateMutation = useUpdateProviderMethod();
  const [form, setForm] = useState({
    provider_name: provider.providerName,
    category: provider.category,
    min_amount_cents: String(provider.minAmountCents),
    max_amount_cents: String(provider.maxAmountCents),
    commission_percentage: String(provider.commissionPercentage),
    commission_fixed_cents: String(provider.commissionFixedCents),
    processing_time: provider.processingTime,
    display_order: String(provider.displayOrder),
    requires_email: provider.requiresEmail,
    requires_phone: provider.requiresPhone,
    requires_bank_account: provider.requiresBankAccount,
    requires_beneficiary_name: provider.requiresBeneficiaryName,
    requires_beneficiary_doc: provider.requiresBeneficiaryDoc,
    requires_wallet_address: provider.requiresWalletAddress,
    requires_network: provider.requiresNetwork,
  });
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError("");

    if (!form.provider_name.trim()) {
      setError("El nombre del proveedor es requerido");
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: provider.id,
        body: {
          provider_name: form.provider_name,
          category: form.category,
          min_amount_cents: Number(form.min_amount_cents),
          max_amount_cents: Number(form.max_amount_cents),
          commission_percentage: Number(form.commission_percentage),
          commission_fixed_cents: Number(form.commission_fixed_cents),
          processing_time: form.processing_time,
          display_order: Number(form.display_order),
          requires_email: form.requires_email,
          requires_phone: form.requires_phone,
          requires_bank_account: form.requires_bank_account,
          requires_beneficiary_name: form.requires_beneficiary_name,
          requires_beneficiary_doc: form.requires_beneficiary_doc,
          requires_wallet_address: form.requires_wallet_address,
          requires_network: form.requires_network,
        },
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al actualizar el proveedor");
    }
  };

  const set = (key: string, value: string | boolean) => setForm(prev => ({...prev, [key]: value}));

  return (
    <BaseModal
      open
      onClose={onClose}
      title={`Editar — ${provider.providerName}`}
      size="xl"
      footer={
        <>
          <BaseButton
            variant="ghost"
            size="md"
            onClick={onClose}
          >
            Cancelar
          </BaseButton>
          <BaseButton
            variant="primary"
            size="md"
            loading={updateMutation.isPending}
            onClick={handleSubmit}
          >
            Guardar
          </BaseButton>
        </>
      }
    >
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4"
      >
        {/* Info fija */}
        <div className="grid grid-cols-2 gap-3 p-3 rounded-md bg-(--color-surface-2) text-xs text-(--color-text-secondary)">
          <div>
            <span className="font-medium">Código:</span> {provider.providerCode}
          </div>
          <div>
            <span className="font-medium">País:</span> {provider.countryName} ({provider.countryCode})
          </div>
          <div>
            <span className="font-medium">Moneda:</span> {provider.currency}
          </div>
          <div>
            <span className="font-medium">Operación:</span>{" "}
            {provider.operationType === "deposit" ? "Depósito" : "Retiro"}
          </div>
        </div>

        <BaseInput
          label="Nombre del proveedor"
          value={form.provider_name}
          onChange={e => set("provider_name", e.target.value)}
        />

        <div className="grid grid-cols-2 gap-3">
          <BaseSelect
            label="Categoría"
            options={categoryOptions}
            value={form.category}
            onChange={e => set("category", e.target.value)}
          />
          <BaseInput
            label="Tiempo procesamiento"
            value={form.processing_time}
            onChange={e => set("processing_time", e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <BaseInput
            label="Monto mínimo (centavos)"
            type="number"
            value={form.min_amount_cents}
            onChange={e => set("min_amount_cents", e.target.value)}
          />
          <BaseInput
            label="Monto máximo (centavos)"
            type="number"
            value={form.max_amount_cents}
            onChange={e => set("max_amount_cents", e.target.value)}
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <BaseInput
            label="Comisión %"
            type="number"
            step="0.01"
            value={form.commission_percentage}
            onChange={e => set("commission_percentage", e.target.value)}
          />
          <BaseInput
            label="Comisión fija (centavos)"
            type="number"
            value={form.commission_fixed_cents}
            onChange={e => set("commission_fixed_cents", e.target.value)}
          />
          <BaseInput
            label="Orden"
            type="number"
            value={form.display_order}
            onChange={e => set("display_order", e.target.value)}
          />
        </div>

        {/* Requires checkboxes */}
        <div>
          <p className="text-sm font-medium text-(--color-text) mb-2">Campos requeridos al usuario</p>
          <div className="grid grid-cols-2 gap-2">
            {(
              [
                ["requires_email", "Email"],
                ["requires_phone", "Teléfono"],
                ["requires_bank_account", "Cuenta bancaria"],
                ["requires_beneficiary_name", "Nombre beneficiario"],
                ["requires_beneficiary_doc", "Documento beneficiario"],
                ["requires_wallet_address", "Dirección wallet"],
                ["requires_network", "Red blockchain"],
              ] as const
            ).map(([key, label]) => (
              <label
                key={key}
                className="flex items-center gap-2 text-sm text-(--color-text-secondary)"
              >
                <input
                  type="checkbox"
                  checked={form[key]}
                  onChange={e => set(key, e.target.checked)}
                  className="rounded border-(--color-border)"
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        {error && <p className="text-sm text-(--color-danger)">{error}</p>}
      </form>
    </BaseModal>
  );
};
